import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { ISecureFile } from '@repo/toolkit/types'
import { CameraIcon, PenIcon, UserFourIcon, WarningIcon, XIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import React, { useRef, useState } from 'react'
import { toast } from 'sonner'

import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { useSecureFile } from '../../../lib/hooks/use-secure-file'

type SecureProfileImageInputProps = {
  title: string
  file?: ISecureFile | null
  toggleClassName?: string
  iconColor?: TailwindColor
  isLoading?: boolean
  maxImageSizeMB?: number
  disabled?: boolean
  onFileChange: (file: File) => void
}

export const SecureProfileImageInput: React.FC<SecureProfileImageInputProps> = ({
  title,
  file,
  toggleClassName,
  iconColor,
  isLoading = false,
  maxImageSizeMB = 2,
  disabled,
  onFileChange
}) => {
  const ref = useRef<HTMLInputElement>(null)
  const { modalState, toggleModal } = useModalManager(['file'])
  const { isLoading: isScanLoading, url, status } = useSecureFile(file?.uploadId)
  const [localUrl, setLocalUrl] = useState<null | string>(null)

  const displayedUrl = localUrl || url

  const isError = status === 'INFECTED' || status === 'FAILED'
  const showLoader = (isLoading || isScanLoading) && !localUrl

  return (
    <div>
      <input
        type="file"
        hidden
        ref={ref}
        accept="image/jpeg,image/png"
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (!file) return

          const maxSizeMB = maxImageSizeMB || 2
          const allowedExtensions = ['image/jpeg', 'image/png']

          if (file.size > maxSizeMB * 1024 * 1024) {
            toast.error('Le fichier ne doit pas dépasser 2 Mo.')
            return
          }

          if (!allowedExtensions.includes(file.type)) {
            toast.error('Seuls les fichiers JPG, JPEG ou PNG sont autorisés.')
            return
          }
          setLocalUrl(URL.createObjectURL(file))
          onFileChange(file)
        }}
      />
      <div className="relative w-fit">
        <div className="rounded-2xl w-20 h-20 object-cover bg-grey-light-hover flex justify-center items-center overflow-hidden">
          {showLoader ? (
            <Loader center size="xs" />
          ) : isError ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-red-50">
              <WarningIcon color={tailwindColors.secondaryRed.DEFAULT} />
              <Typography variant="tag" color={tailwindColors.secondaryRed.DEFAULT}>
                {status === 'INFECTED' ? 'Virus détecté' : 'Échec analyse'}
              </Typography>
            </div>
          ) : file && displayedUrl ? (
            <img
              src={displayedUrl}
              alt="profile"
              className="w-full h-full object-cover rounded-2xl cursor-pointer"
              onClick={() => toggleModal('file')}
            />
          ) : (
            <UserFourIcon filled className="w-full h-full" color={tailwindColors.white.DEFAULT} />
          )}
        </div>
        {!disabled && (
          <div
            className="bg-white w-8 h-8 rounded-full absolute bottom-1 -right-2 flex justify-center items-center cursor-pointer group p-[2px]"
            onClick={() => ref.current?.click()}
          >
            <div
              className={cn(
                'w-full h-full rounded-full flex justify-center items-center p-[2px]',
                {
                  'bg-primary': !file,
                  'bg-secondary-light': !!file
                },
                toggleClassName
              )}
            >
              {isLoading ? (
                <Loader center size="xs" />
              ) : file ? (
                <PenIcon
                  className="w-4 h-4 "
                  color={iconColor ?? tailwindColors.secondary.DEFAULT}
                  fill={tailwindColors.white.DEFAULT}
                />
              ) : (
                <CameraIcon
                  color={iconColor ?? tailwindColors.white.DEFAULT}
                  className="w-full h-full"
                />
              )}
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalState.file}
        onCancel={() => toggleModal('file')}
        title={title}
        hideHeader
        hideActions
      >
        {file && url && (
          <div className="relative">
            <img src={url} alt="profile" className=" w-full rounded-xl h-96 object-contain" />
            <button
              aria-label="Close"
              className={cn(
                'p-1 hover:bg-grey-light-hover/50 rounded-full transition-colors duration-200 ease-in-out absolute top-0 right-0 m-4'
              )}
              onClick={() => toggleModal('file')}
            >
              <XIcon color={tailwindColors.black.DEFAULT} height="15px" width="15px" />
            </button>
          </div>
        )}
      </Modal>
    </div>
  )
}
